import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import PasswordField from './PasswordField';

const BaseInput = (props) => {
  const {
    valid,
    touched,
    validation,
    errorDescription,
    description,
    config,
    value,
    onChange,
    onlineValid,
    corrected,
    correctedMessage,
    username,
  } = props;

  const inputIsSuccess = 'is-success';
  const inputIsDanger = 'is-danger';
  const inputClasses = ['input', 'is-medium'];
  const errorDescriptionLabel = errorDescription || 'This field is invalid';
  const iconIsInProgress = 'fas fa-spinner fa-spin';
  const iconIsSuccess = 'fas fa-check has-text-success';
  const iconIsDanger = 'fas fa-exclamation-triangle has-text-danger';
  const { t } = useTranslation();

  let rightIcon;
  let warningMessage;
  let patternAsString;

  function patternToString(pattern) {
    if (pattern) {
      patternAsString = pattern.toString().substr(1);

      return patternAsString.substring(0, patternAsString.length - 1);
    }
    return '';
  }

  if (!valid && touched && validation.required) {
    inputClasses.push(inputIsDanger);
    rightIcon = iconIsDanger;
    warningMessage = <p className="help is-danger">{errorDescriptionLabel}</p>;
  } else if (valid && touched && validation.required && !onlineValid && validation.inline) {
    rightIcon = iconIsInProgress;
  } else if (valid && touched && validation.required) {
    inputClasses.push(inputIsSuccess);
    rightIcon = iconIsSuccess;
  }

  if (config.type === 'password' && valid && touched && validation.required && validation.validatePasswordUsernameCheck) {
    const passwordCheckResult = validation.validatePasswordUsernameCheck(value, username);
    if (passwordCheckResult !== true) {
      inputClasses.push(inputIsDanger);
      rightIcon = iconIsDanger;
      warningMessage = <p className="help is-danger">{t(passwordCheckResult)}</p>;
    } else {
      rightIcon = iconIsSuccess;
    }
  }

  // Prepared correct regex for HTML5
  patternAsString = patternToString(validation.pattern);

  let input = (
    <input
      className={inputClasses.join(' ')}
      type={config.type}
      value={value}
      onChange={onChange}
    />
  );

  const isPassword = config.type === 'password';

  const correctedMessageContainer = (
    <p className="help is-success">
      {correctedMessage}
    </p>
  );

  if (isPassword) {
    input = (
      <PasswordField
        className={inputClasses.join(' ')}
        type={config.type}
        value={value}
        onChange={onChange}
      />
    );
  }

  return (
    // const { description, validation, config, value, onChange } = props;
    <div className="column is-6 field">
      <label className="label">{description}</label>
      <div className={`control has-icons-left has-icons-right${isPassword ? ' custom-password-field multiple-icons' : ''}`}>
        {input}
        <span className="icon is-small is-left">
          <i className={config.leftIcon} />
        </span>
        <span className="icon is-small is-right">
          <i className={rightIcon} />
        </span>
      </div>
      {corrected && correctedMessageContainer}
      {warningMessage}
    </div>
  );
};

BaseInput.defaultProps = {
  valid: false,
  touched: false,
  validation: {
    required: false,
    pattern: null,
    inline: false,
    validatePasswordUsernameCheck: null,
  },
  errorDescription: '',
  description: '',
  onlineValid: false,
  corrected: false,
  correctedMessage: '',
  username: '',
  value: '',
};

BaseInput.propTypes = {
  valid: PropTypes.bool,
  touched: PropTypes.bool,
  validation: PropTypes.shape({
    required: PropTypes.bool,
    pattern: PropTypes.instanceOf(RegExp),
    inline: PropTypes.bool,
    validatePasswordUsernameCheck: PropTypes.func,
  }),
  errorDescription: PropTypes.string,
  description: PropTypes.string,
  config: PropTypes.shape({
    type: PropTypes.string.isRequired,
    leftIcon: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onlineValid: PropTypes.bool,
  corrected: PropTypes.bool,
  correctedMessage: PropTypes.string,
  username: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

export default BaseInput;
